import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  CaretLeft,
  SealCheck,
  ShieldCheck,
  Truck,
  Clock,
  CreditCard,
  Minus,
  Plus,
} from "@phosphor-icons/react";
import Layout from "@/components/Layout";
import { getProduct, products } from "@/data/mockData";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { usePaymentStatus } from "@/hooks/usePaymentStatus";

const typeLabel = {
  original: "ORIGINAL",
  print: "PRINT",
  nft: "NFT",
  commission: "COMISIÓN",
};

const typeColor = {
  original: "#FF3E00",
  print: "#00FF66",
  nft: "#0033FF",
  commission: "#FF0033",
};

function fmt(price, currency) {
  if (currency === "ETH") return `Ξ ${price}`;
  return `${currency === "USD" ? "$" : "€"}${price.toLocaleString("es")}`;
}

export default function ProductDetail() {
  const { id } = useParams();
  const product = getProduct(id);
  const [qty, setQty] = useState(1);
  const [buying, setBuying] = useState(false);
  const [buyErr, setBuyErr] = useState("");
  const { user } = useAuth();
  const payment = usePaymentStatus();
  const related = products
    .filter((p) => p.id !== product.id && p.artist.slug === product.artist.slug)
    .slice(0, 3);

  const fee = Math.round(product.price * 0.08 * 100) / 100;
  const artistGets = Math.round((product.price - fee) * 100) / 100;

  const canBuyWithStripe =
    product.currency !== "ETH" && product.type !== "nft"; // NFTs out of scope this sprint

  const buy = async () => {
    setBuying(true);
    setBuyErr("");
    try {
      const { data } = await api.post("/payments/orders/checkout", {
        product_id: product.id,
        quantity: qty,
        origin: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      setBuyErr(formatApiError(e));
      setBuying(false);
    }
  };

  return (
    <Layout>
      <div data-testid="product-page">
        <header className="flex items-center gap-4 p-4 md:p-6 border-b border-[#27272A]">
          <Link
            to="/tienda"
            data-testid="back-to-shop"
            className="h-9 w-9 border border-[#27272A] hover:border-[#FF3E00] inline-flex items-center justify-center"
          >
            <CaretLeft size={16} />
          </Link>
          <div className="font-mono text-[10px] text-[#A1A1AA] tracking-[0.22em]">
            TIENDA / {typeLabel[product.type]} / {product.id.toUpperCase()}
          </div>
        </header>

        <div className="grid grid-cols-1 lg:grid-cols-2">
          {/* Image */}
          <div className="relative bg-[#0a0a0a] border-b lg:border-b-0 lg:border-r border-[#27272A]">
            <div className="relative aspect-square lg:aspect-auto lg:min-h-[720px]">
              <img
                src={product.image}
                alt={product.title}
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute top-4 left-4">
                <span
                  className={`font-mono text-[10px] uppercase tracking-[0.22em] px-2 py-1 ${
                    typeColor[product.type] === "#FF3E00" || typeColor[product.type] === "#FF0033" || typeColor[product.type] === "#0033FF"
                      ? "text-white"
                      : "text-black"
                  }`}
                  style={{ background: typeColor[product.type] }}
                >
                  {typeLabel[product.type]}
                </span>
              </div>
            </div>
          </div>

          {/* Buy panel */}
          <div className="p-5 md:p-10 space-y-6">
            <div>
              <Link
                to={`/artista/${product.artist.slug}`}
                className="inline-flex items-center gap-2 mb-4 hover:text-[#FF3E00]"
              >
                <img
                  src={product.artist.avatar}
                  alt={product.artist.name}
                  className="h-8 w-8 object-cover border border-[#27272A]"
                />
                <span className="font-display font-bold flex items-center gap-1">
                  {product.artist.name}
                  {product.artist.verified ? (
                    <SealCheck size={12} weight="fill" className="text-[#FF3E00]" />
                  ) : null}
                </span>
                <span className="font-mono text-[10px] text-[#A1A1AA] tracking-wider">
                  @{product.artist.handle}
                </span>
              </Link>

              <h1 className="font-display text-3xl md:text-5xl font-black tracking-tighter uppercase leading-[1.05]">
                {product.title}
              </h1>

              <div className="flex items-end gap-4 mt-6">
                <div className="font-display font-black text-5xl md:text-6xl text-[#FF3E00] leading-none">
                  {fmt(product.price, product.currency)}
                </div>
                {product.sold > 0 ? (
                  <div className="font-mono text-[10px] text-[#A1A1AA] tracking-wider pb-2">
                    // {product.sold} ya lo compraron
                  </div>
                ) : null}
              </div>
            </div>

            <p className="text-[15px] leading-[1.6] text-[#E5E5E5]">
              {product.description}
            </p>

            {/* Specs */}
            <dl className="grid grid-cols-2 gap-0 border border-[#27272A]">
              <SpecRow k="EDICIÓN" v={product.edition} full />
              {product.shipping ? <SpecRow k="ENVÍO" v={product.shipping} /> : null}
              {product.turnaround ? <SpecRow k="ENTREGA" v={product.turnaround} /> : null}
              <SpecRow k="AUTENTICIDAD" v="Blockchain · certificado digital" />
              <SpecRow k="GARANTÍA" v="30 días · devolución completa" />
            </dl>

            {/* Quantity + Buy */}
            {product.type !== "commission" ? (
              <div className="flex items-center gap-3 border border-[#27272A] w-fit">
                <button
                  onClick={() => setQty((q) => Math.max(1, q - 1))}
                  className="h-12 w-12 inline-flex items-center justify-center text-[#A1A1AA] hover:text-white hover:bg-[#121212]"
                >
                  <Minus size={14} />
                </button>
                <span className="font-display font-black text-xl w-10 text-center">
                  {qty}
                </span>
                <button
                  onClick={() => setQty((q) => q + 1)}
                  className="h-12 w-12 inline-flex items-center justify-center text-[#A1A1AA] hover:text-white hover:bg-[#121212]"
                >
                  <Plus size={14} />
                </button>
              </div>
            ) : null}

            <div className="flex flex-col sm:flex-row gap-3">
              <button
                data-testid="buy-btn"
                onClick={buy}
                disabled={buying || !canBuyWithStripe || !user}
                title={
                  !user
                    ? "Inicia sesión para comprar"
                    : !canBuyWithStripe
                    ? "NFTs requieren wallet crypto (próximamente)"
                    : ""
                }
                className={`btn-brutal flex-1 font-mono uppercase text-xs tracking-[0.22em] font-bold py-4 inline-flex items-center justify-center gap-2 ${
                  buying || !canBuyWithStripe || !user
                    ? "bg-[#121212] text-[#525252]"
                    : "bg-[#FF3E00] text-white"
                }`}
              >
                <CreditCard size={14} weight="bold" />
                {buying
                  ? "Redirigiendo a Stripe…"
                  : !user
                  ? "Inicia sesión para comprar"
                  : !canBuyWithStripe
                  ? "NFT · próximamente"
                  : product.type === "commission"
                  ? "Solicitar comisión"
                  : "Comprar ahora"}
              </button>
              <button className="btn-brutal font-mono uppercase text-xs tracking-[0.22em] px-6 py-4 border border-[#F4F4F5] hover:bg-[#F4F4F5] hover:text-black">
                ♥ Guardar
              </button>
            </div>

            {buyErr ? (
              <div className="border border-[#FF0033] bg-[#FF0033]/10 px-3 py-2 font-mono text-[11px] text-[#FF8899]">
                {buyErr}
              </div>
            ) : null}
            {payment.state === "paid" && payment.kind === "order" ? (
              <div className="border border-[#00FF66] bg-[#00FF66]/10 px-4 py-3 font-mono text-[11px] text-[#00FF66] tracking-wider">
                ✓ COMPRA CONFIRMADA · Te contactaremos por email con los
                detalles de envío.
              </div>
            ) : null}
            {payment.state === "polling" ? (
              <div className="border border-[#FF3E00] bg-[#FF3E00]/10 px-4 py-3 font-mono text-[11px] text-[#FFAA88] tracking-wider">
                · Verificando pago con Stripe…
              </div>
            ) : null}

            {/* Trust strip */}
            <div className="grid grid-cols-3 gap-3 pt-2">
              <TrustItem icon={ShieldCheck} label="Compra segura" />
              <TrustItem icon={Truck} label="Envío con tracking" />
              <TrustItem icon={Clock} label="Respuesta en 24h" />
            </div>

            {/* Artist earning breakdown */}
            <div className="border border-[#27272A] p-4 bg-[#0a0a0a]">
              <div className="font-mono text-[10px] text-[#A1A1AA] tracking-[0.22em] mb-3">
                // ¿A DÓNDE VA TU DINERO?
              </div>
              <div className="space-y-2 font-mono text-[11px] tracking-wider">
                <div className="flex justify-between">
                  <span className="text-[#A1A1AA]">AL ARTISTA (92%)</span>
                  <span className="text-[#00FF66]">
                    {fmt(artistGets, product.currency)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#A1A1AA]">PLATAFORMA (8%)</span>
                  <span className="text-[#FF3E00]">{fmt(fee, product.currency)}</span>
                </div>
                <div className="h-1 bg-[#27272A] flex mt-2">
                  <div className="bg-[#00FF66]" style={{ width: "92%" }} />
                  <div className="bg-[#FF3E00]" style={{ width: "8%" }} />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Related */}
        {related.length > 0 ? (
          <section className="p-4 md:p-6 border-t border-[#27272A]">
            <h2 className="font-display font-black uppercase tracking-tight text-xl mb-4">
              Más de {product.artist.name}
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {related.map((p) => (
                <Link
                  key={p.id}
                  to={`/tienda/${p.id}`}
                  className="group block border border-[#27272A] hover:border-[#FF3E00] bg-[#0a0a0a]"
                >
                  <div className="relative aspect-[4/5] overflow-hidden">
                    <img
                      src={p.image}
                      alt={p.title}
                      className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all"
                    />
                  </div>
                  <div className="p-3 border-t border-[#27272A] flex items-center justify-between gap-2">
                    <span className="font-display font-bold text-sm truncate">
                      {p.title}
                    </span>
                    <span className="font-display font-black text-[#FF3E00] text-sm whitespace-nowrap">
                      {fmt(p.price, p.currency)}
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        ) : null}
      </div>
    </Layout>
  );
}

function SpecRow({ k, v, full }) {
  return (
    <div
      className={`p-4 border-t border-[#27272A] first:border-t-0 ${
        full ? "col-span-2" : ""
      }`}
    >
      <div className="font-mono text-[10px] text-[#A1A1AA] tracking-[0.22em]">
        {k}
      </div>
      <div className="font-display font-semibold text-sm mt-1">{v}</div>
    </div>
  );
}

function TrustItem({ icon: Icon, label }) {
  return (
    <div className="border border-[#27272A] p-3 flex flex-col items-center gap-1 text-center">
      <Icon size={16} className="text-[#00FF66]" />
      <span className="font-mono text-[9px] uppercase tracking-[0.22em] text-[#A1A1AA]">
        {label}
      </span>
    </div>
  );
}
